<template>
  <div>
    <!-- 页面标题 -->
    <el-breadcrumb separator-class="el-icon-arrow-right" style="margin-bottom: 20px;">
      <el-breadcrumb-item>财务管理</el-breadcrumb-item>
      <el-breadcrumb-item>财务总览</el-breadcrumb-item>
    </el-breadcrumb>

    <!-- 日期选择器 -->
    <el-card class="date-selector" style="margin-bottom: 20px;">
      <div slot="header">
        <span>统计时间范围</span>
      </div>
      <el-date-picker
        v-model="dateRange"
        type="daterange"
        range-separator="至"
        start-placeholder="开始日期"
        end-placeholder="结束日期"
        format="yyyy-MM-dd"
        value-format="yyyy-MM-dd"
        @change="loadStatistics"
        style="margin-right: 20px;">
      </el-date-picker>
      <el-button-group>
        <el-button size="small" @click="setDateRange('today')">今日</el-button>
        <el-button size="small" @click="setDateRange('week')">本周</el-button>
        <el-button size="small" @click="setDateRange('month')">本月</el-button>
        <el-button size="small" @click="setDateRange('year')">本年</el-button>
      </el-button-group>
    </el-card>

    <!-- KPI 卡片 -->
    <el-row :gutter="20" style="margin-bottom: 20px;">
      <el-col :span="5">
        <el-card class="kpi-card">
          <div class="kpi-content">
            <div class="kpi-title">总收入</div>
            <div class="kpi-value income">¥{{ formatMoney(statistics.totalIncome) }}</div>
          </div>
          <i class="el-icon-coin kpi-icon income-icon"></i>
        </el-card>
      </el-col>
      <el-col :span="5">
        <el-card class="kpi-card">
          <div class="kpi-content">
            <div class="kpi-title">总支出</div>
            <div class="kpi-value expense">¥{{ formatMoney(statistics.totalExpense) }}</div>
          </div>
          <i class="el-icon-sell kpi-icon expense-icon"></i>
        </el-card>
      </el-col>
      <el-col :span="5">
        <el-card class="kpi-card">
          <div class="kpi-content">
            <div class="kpi-title">总退款</div>
            <div class="kpi-value refund">¥{{ formatMoney(statistics.totalRefunds) }}</div>
          </div>
          <i class="el-icon-refresh-left kpi-icon refund-icon"></i>
        </el-card>
      </el-col>
      <el-col :span="5">
        <el-card class="kpi-card">
          <div class="kpi-content">
            <div class="kpi-title">净利润</div>
            <div class="kpi-value" :class="statistics.profit >= 0 ? 'profit' : 'loss'">
              ¥{{ formatMoney(statistics.profit) }}
            </div>
          </div>
          <i class="el-icon-data-line kpi-icon profit-icon"></i>
        </el-card>
      </el-col>
      <el-col :span="4">
        <el-card class="kpi-card">
          <div class="kpi-content">
            <div class="kpi-title">订单数量</div>
            <div class="kpi-value">{{ statistics.totalOrders || 0 }}</div>
          </div>
          <i class="el-icon-document kpi-icon"></i>
        </el-card>
      </el-col>
    </el-row>

    <!-- 图表区域 -->
    <el-row :gutter="20" style="margin-bottom: 20px;">
      <!-- 收入趋势图 -->
      <el-col :span="12">
        <el-card>
          <div slot="header">
            <span>收入趋势</span>
          </div>
          <div id="incomeChart" style="height: 300px;"></div>
        </el-card>
      </el-col>
      <!-- 渠道占比图 -->
      <el-col :span="12">
        <el-card>
          <div slot="header">
            <span>支付渠道占比</span>
          </div>
          <div id="channelChart" style="height: 300px;"></div>
        </el-card>
      </el-col>
    </el-row>

    <!-- 详细统计 -->
    <el-row :gutter="20">
      <!-- 影片收入排行 -->
      <el-col :span="12">
        <el-card>
          <div slot="header">
            <span>影片收入排行</span>
          </div>
          <el-table :data="statistics.movieIncomeRanking || []" height="250">
            <el-table-column prop="movieName" label="影片名称" show-overflow-tooltip></el-table-column>
            <el-table-column prop="income" label="收入" width="120">
              <template slot-scope="scope">
                ¥{{ formatMoney(scope.row.income) }}
              </template>
            </el-table-column>
            <el-table-column prop="ticketCount" label="票数" width="80"></el-table-column>
          </el-table>
        </el-card>
      </el-col>
      <!-- 支出分类统计 -->
      <el-col :span="12">
        <el-card>
          <div slot="header">
            <span>支出分类统计</span>
          </div>
          <div id="expenseChart" style="height: 250px;"></div>
        </el-card>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import * as echarts from 'echarts'

export default {
  name: "FinanceOverview",
  data() {
    return {
      dateRange: [],
      statistics: {
        totalIncome: 0,
        totalExpense: 0,
        totalRefunds: 0,
        profit: 0,
        totalOrders: 0,
        totalTickets: 0,
        avgTicketPrice: 0,
        alipayIncome: 0,
        mockIncome: 0,
        channelBreakdown: {},
        dailyTrends: [],
        movieIncomeRanking: [],
        expenseCategoryBreakdown: {}
      },
      incomeChart: null,
      channelChart: null,
      expenseChart: null
    }
  },
  mounted() {
    this.setDateRange('month') // 默认本月
    this.initCharts()
  },
  beforeDestroy() {
    if (this.incomeChart) this.incomeChart.dispose()
    if (this.channelChart) this.channelChart.dispose()
    if (this.expenseChart) this.expenseChart.dispose()
  },
  methods: {
    setDateRange(type) {
      const today = new Date()
      let startDate, endDate

      switch (type) {
        case 'today':
          startDate = endDate = this.formatDate(today)
          break
        case 'week':
          const monday = new Date(today)
          // 获取今天是星期几（0=周日, 1=周一, ..., 6=周六）
          const dayOfWeek = today.getDay()
          // 计算本周周一的日期：如果今天是周日(0)，则减去6天；否则减去(dayOfWeek-1)天
          const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1
          monday.setDate(today.getDate() - daysToSubtract)
          startDate = this.formatDate(monday)
          endDate = this.formatDate(today)
          break
        case 'month':
          startDate = this.formatDate(new Date(today.getFullYear(), today.getMonth(), 1))
          endDate = this.formatDate(today)
          break
        case 'year':
          startDate = this.formatDate(new Date(today.getFullYear(), 0, 1))
          endDate = this.formatDate(today)
          break
      }

      this.dateRange = [startDate, endDate]
      this.loadStatistics()
    },

    formatDate(date) {
      return date.toISOString().split('T')[0]
    },

    async loadStatistics() {
      if (!this.dateRange || this.dateRange.length !== 2) return

      try {
        const params = {
          startDate: this.dateRange[0],
          endDate: this.dateRange[1]
        }
        
        const { data: res } = await axios.get('api/admin/finance/statistics', { params })
        if (res.code === 200) {
          this.statistics = res.data
          this.updateCharts()
        } else {
          this.$message.error('获取财务统计失败: ' + res.message)
        }
      } catch (error) {
        console.error('加载财务统计失败:', error)
        this.$message.error('加载财务统计失败')
      }
    },

    initCharts() {
      this.incomeChart = echarts.init(document.getElementById('incomeChart'))
      this.channelChart = echarts.init(document.getElementById('channelChart'))
      this.expenseChart = echarts.init(document.getElementById('expenseChart'))
    },

    updateCharts() {
      this.updateIncomeChart()
      this.updateChannelChart()
      this.updateExpenseChart()
    },

    updateIncomeChart() {
      if (!this.incomeChart) return

      const dailyTrends = this.statistics.dailyTrends || []
      const dates = dailyTrends.map(item => item.date ? item.date.split('T')[0] : '')
      const incomes = dailyTrends.map(item => item.income || 0)
      const expenses = dailyTrends.map(item => item.expense || 0)

      const option = {
        title: {
          text: '收支趋势',
          left: 'center'
        },
        tooltip: {
          trigger: 'axis',
          formatter: function(params) {
            let result = params[0].name + '<br/>'
            params.forEach(param => {
              result += param.seriesName + ': ¥' + param.value + '<br/>'
            })
            return result
          }
        },
        legend: {
          data: ['收入', '支出'],
          bottom: 10
        },
        xAxis: {
          type: 'category',
          data: dates
        },
        yAxis: {
          type: 'value',
          axisLabel: {
            formatter: '¥{value}'
          }
        },
        series: [
          {
            name: '收入',
            type: 'line',
            data: incomes,
            itemStyle: { color: '#67C23A' }
          },
          {
            name: '支出',
            type: 'line',
            data: expenses,
            itemStyle: { color: '#F56C6C' }
          }
        ]
      }

      this.incomeChart.setOption(option)
    },

    updateChannelChart() {
      if (!this.channelChart) return

      const channelData = []
      const breakdown = this.statistics.channelBreakdown || {}
      
      Object.keys(breakdown).forEach(channel => {
        let name
        switch(channel) {
          case 'ALIPAY':
            name = '支付宝'
            break
          case 'MOCK_WECHAT':
            name = '模拟微信支付'
            break
          case 'MOCK_ALIPAY':
            name = '模拟支付宝支付'
            break
          case 'MOCK':
            name = '模拟支付(旧)'
            break
          case 'MANUAL':
            name = '手工录入'
            break
          default:
            name = channel
        }
        
        const value = breakdown[channel] || 0
        if (value > 0) {  // 只显示有数据的渠道
          channelData.push({
            name: name,
            value: value
          })
        }
      })

      const option = {
        title: {
          text: '支付渠道占比',
          left: 'center'
        },
        tooltip: {
          trigger: 'item',
          formatter: '{a} <br/>{b}: ¥{c} ({d}%)'
        },
        series: [
          {
            name: '支付渠道',
            type: 'pie',
            radius: '60%',
            data: channelData,
            emphasis: {
              itemStyle: {
                shadowBlur: 10,
                shadowOffsetX: 0,
                shadowColor: 'rgba(0, 0, 0, 0.5)'
              }
            }
          }
        ]
      }

      this.channelChart.setOption(option)
    },

    updateExpenseChart() {
      if (!this.expenseChart) return

      const expenseData = []
      const breakdown = this.statistics.expenseCategoryBreakdown || {}
      
      Object.keys(breakdown).forEach(category => {
        const name = this.getCategoryName(category)
        expenseData.push({
          name: name,
          value: breakdown[category] || 0
        })
      })

      const option = {
        title: {
          text: '支出分类',
          left: 'center'
        },
        tooltip: {
          trigger: 'item',
          formatter: '{a} <br/>{b}: ¥{c} ({d}%)'
        },
        series: [
          {
            name: '支出分类',
            type: 'pie',
            radius: ['40%', '70%'],
            data: expenseData,
            emphasis: {
              itemStyle: {
                shadowBlur: 10,
                shadowOffsetX: 0,
                shadowColor: 'rgba(0, 0, 0, 0.5)'
              }
            },
            label: {
              show: true,
              formatter: '{b}: {d}%'
            }
          }
        ]
      }

      this.expenseChart.setOption(option)
    },

    getCategoryName(category) {
      const categoryMap = {
        'FILM_COST': '影片成本',
        'EQUIPMENT': '设备维护',
        'SALARY': '员工工资',
        'RENT': '租金',
        'OTHER': '其他'
      }
      return categoryMap[category] || category
    },

    formatMoney(amount) {
      if (!amount && amount !== 0) return '0.00'
      return Number(amount).toFixed(2)
    }
  }
}
</script>

<style scoped>
.date-selector {
  text-align: left;
}

.kpi-card {
  position: relative;
  overflow: hidden;
}

.kpi-content {
  position: relative;
  z-index: 2;
}

.kpi-title {
  font-size: 14px;
  color: #666;
  margin-bottom: 8px;
}

.kpi-value {
  font-size: 24px;
  font-weight: bold;
  margin-bottom: 5px;
}

.kpi-value.income {
  color: #67C23A;
}

.kpi-value.expense {
  color: #F56C6C;
}

.kpi-value.profit {
  color: #409EFF;
}

.kpi-value.loss {
  color: #F56C6C;
}

.kpi-value.refund {
  color: #E6A23C;
}

.kpi-icon {
  position: absolute;
  right: 20px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 40px;
  opacity: 0.3;
  z-index: 1;
}

.income-icon {
  color: #67C23A;
}

.expense-icon {
  color: #F56C6C;
}

.refund-icon {
  color: #E6A23C;
}

.profit-icon {
  color: #409EFF;
}
</style>
